export const VR_CADENCES = ['welcome', 'daily', 'weekly', 'monthly', 'event'];
export const VR_CADENCE_LABEL = {
    welcome: 'Welcome',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    event: 'Event',
};
export const VR_KINDS = ['cash', 'bonus', 'rebate'];
export const VR_KIND_LABEL = {
    cash: 'Cash',
    bonus: 'Trading bonus',
    rebate: 'Fee & spread rebate',
};
export const VR_CLASSES = ['CRYPTO', 'STOCK', 'INDEX', 'COMMODITY', 'FX', 'MEME'];
export const VR_CLASS_LABEL = {
    CRYPTO: 'Crypto',
    STOCK: 'Stocks',
    INDEX: 'Indices',
    COMMODITY: 'Commodities',
    FX: 'FX',
    MEME: 'Meme coins',
};
/** Approved 2026-10-12 (D4): every class stays at or below 40 % of its minimum trading cost. Meme coins don't count unless an admin says so. */
export const DEFAULT_VR_WEIGHTS = { CRYPTO: 100, STOCK: 50, INDEX: 40, COMMODITY: 40, FX: 20, MEME: 0 };
/** Positions closed sooner than this don't count, as for missions. */
export const DEFAULT_VR_MIN_HOLD_SECONDS = 120;
export const VR_CAMPAIGN_STATUSES = ['draft', 'live', 'paused', 'ended'];
export const VR_USER_STATUSES = ['enabled', 'blocked'];
// ---------- wording ----------
const money = (n) => `$${Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const days = (n) => `${n} ${n === 1 ? 'day' : 'days'}`;
/** "$8 cash", "$25 trading bonus", "20% fee & spread rebate". */
export function stepReward(s) {
    if (s.kind === 'rebate')
        return `${s.pct}% fee & spread rebate`;
    return `${money(s.usd)} ${s.kind === 'cash' ? 'cash' : 'trading bonus'}`;
}
/** Chip text: "$8 cash", "$25 bonus", "20% rebate". */
export function stepRewardShort(s) {
    if (s.kind === 'rebate')
        return `${s.pct}% rebate`;
    return `${money(s.usd)} ${s.kind === 'cash' ? 'cash' : 'bonus'}`;
}
/** What the reward does, in one line. */
export function stepDetail(s) {
    switch (s.kind) {
        case 'cash':
            return 'USDT added to your live account when you claim. You can withdraw it.';
        case 'bonus':
            return `Trading credit usable as margin for ${days(s.days ?? 0)} after you claim. It can't be withdrawn; profits made with it can.`;
        case 'rebate':
            return `${s.pct}% of the fees and spread you pay on live trades back for ${days(s.days ?? 0)} after you claim, up to ${money(s.usd)}, as cashback.`;
    }
}
/** "$10K", "$1.5M". */
export function volumeShort(v) {
    if (v >= 1_000_000)
        return `$${+(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1000)
        return `$${+(v / 1000).toFixed(1)}K`;
    return `$${Math.round(v)}`;
}
// ---------- periods (UTC) ----------
const DAY_MS = 86_400_000;
const FAR = new Date('9999-01-01T00:00:00Z');
const ymd = (d) => d.toISOString().slice(0, 10);
/** Monday 00:00 UTC of the week holding `t`. */
function mondayOf(t) {
    const day = Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
    const dow = (new Date(day).getUTCDay() + 6) % 7;
    return new Date(day - dow * DAY_MS);
}
/**
 * The period holding `t`. Daily, weekly and monthly periods repeat; a welcome period runs
 * `welcomeDays` from the first deposit (null without one); an event is its whole run.
 */
export function periodOf(cadence, t, c, firstDeposit) {
    switch (cadence) {
        case 'daily': {
            const start = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()));
            return { key: ymd(start), start, end: new Date(start.getTime() + DAY_MS) };
        }
        case 'weekly': {
            const start = mondayOf(t);
            return { key: ymd(start), start, end: new Date(start.getTime() + 7 * DAY_MS) };
        }
        case 'monthly': {
            const start = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 1));
            return { key: ymd(start), start, end: new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 1)) };
        }
        case 'welcome': {
            if (!firstDeposit || !c.welcomeDays)
                return null;
            return { key: ymd(firstDeposit), start: firstDeposit, end: new Date(firstDeposit.getTime() + c.welcomeDays * DAY_MS) };
        }
        case 'event':
            return { key: ymd(c.startsAt), start: c.startsAt, end: c.endsAt ?? FAR };
    }
}
/** The period before `p`, for repeating cadences (so a step reached just before the rollover is still stored). */
export function previousPeriod(cadence, p) {
    if (cadence !== 'daily' && cadence !== 'weekly' && cadence !== 'monthly')
        return null;
    return periodOf(cadence, new Date(p.start.getTime() - 1), { startsAt: p.start, endsAt: null });
}
/** The part of a period inside the campaign's run, up to `now`: the window whose volume counts. Null when they don't overlap. */
export function countingWindow(p, c, now) {
    const start = new Date(Math.max(p.start.getTime(), c.startsAt.getTime()));
    const end = new Date(Math.min(p.end.getTime(), (c.endsAt ?? FAR).getTime(), now.getTime()));
    return end > start ? { start, end } : null;
}
// ---------- counted volume and cost ----------
/** Raw volume per asset class at its weight, summed. For display; the API does the same in decimals. */
export function countedVolume(raw, weights) {
    let t = 0;
    for (const [k, v] of Object.entries(raw))
        t += (v ?? 0) * ((weights[k] ?? 0) / 100);
    return t;
}
/** A market's minimum trading cost per fill in basis points of notional: commission, half the minimum spread and the markup. */
export const instrumentCostBps = (feeRate, spreadBps, markupBps) => feeRate * 10_000 + spreadBps / 2 + markupBps;
/** The cheapest market to trade in each class (among `symbols` when the campaign names some): the one a client farming rewards would use. */
export function classCosts(instruments, symbols = []) {
    const only = new Set(symbols.map((s) => s.toUpperCase()));
    const out = {};
    for (const i of instruments) {
        if (only.size && !only.has(i.symbol.toUpperCase()))
            continue;
        const cur = out[i.assetClass];
        if (cur === undefined || i.costBps < cur)
            out[i.assetClass] = i.costBps;
    }
    return out;
}
/**
 * What a ladder pays at its richest step, as a share of the cheapest trading cost per class. Steps add
 * up, so the rate at a step is everything paid up to it over its volume. Cash and rebates over 100 %
 * of the cost are refused (trading only for rewards would make money); a trading bonus above it is
 * allowed but flagged, since it can't be withdrawn.
 */
export function costCheck(steps, weights, costs) {
    let cumCash = 0;
    let cumAll = 0;
    let cashBps = 0;
    let allBps = 0;
    for (const s of steps) {
        if (!(s.volume > 0))
            continue;
        const usd = Number.isFinite(s.usd) ? s.usd : 0;
        if (s.kind !== 'bonus')
            cumCash += usd;
        cumAll += usd;
        cashBps = Math.max(cashBps, (cumCash / s.volume) * 10_000);
        allBps = Math.max(allBps, (cumAll / s.volume) * 10_000);
    }
    const classes = [];
    let refused = null;
    for (const k of VR_CLASSES) {
        const w = weights[k] ?? 0;
        const cost = costs[k];
        if (!(w > 0) || cost === undefined)
            continue;
        const share = (bps) => (cost > 0 ? ((bps * w) / 100 / cost) * 100 : bps > 0 ? Infinity : 0);
        const c = { assetClass: k, weight: w, costBps: cost, cashShare: share(cashBps), allShare: share(allBps) };
        classes.push(c);
        if (!refused && c.cashShare > 100) {
            refused = `${VR_CLASS_LABEL[k]}: cash and rebates would pay ${Number.isFinite(c.cashShare) ? Math.round(c.cashShare) : '∞'}% of what the trading costs. Lower the rewards or the weight.`;
        }
    }
    return { cashBps, allBps, classes, refused };
}
// ---------- validation ----------
const CC_RE = /^[A-Z]{2}$/;
const SYMBOL_RE = /^[A-Z0-9._-]{1,24}$/;
const TIER_RE = /^[a-z0-9_]{1,20}$/;
const MAX_USD = 10_000;
/** Returns an error message, or null when the campaign is valid. `tierIds`: the Earn tiers a minimum tier may name. */
export function validateCampaign(c, tierIds) {
    if (!VR_CADENCES.includes(c.cadence))
        return 'Choose how often the campaign runs';
    if (!c.name?.trim() || c.name.trim().length > 60)
        return 'Enter a name (up to 60 characters)';
    if ((c.description ?? '').length > 240)
        return 'Keep the description to 240 characters';
    if (!VR_CAMPAIGN_STATUSES.includes(c.status))
        return 'Unknown status';
    const r = c.rules;
    if (!r || typeof r !== 'object')
        return 'Set how volume counts';
    for (const k of VR_CLASSES) {
        const w = r.weights?.[k];
        if (!(typeof w === 'number' && w >= 0 && w <= 100))
            return `${VR_CLASS_LABEL[k]}: weight must be 0–100%`;
    }
    if (!VR_CLASSES.some((k) => r.weights[k] > 0))
        return 'At least one asset class must count';
    if (!Array.isArray(r.symbols) || r.symbols.length > 100 || r.symbols.some((x) => typeof x !== 'string' || !SYMBOL_RE.test(x)))
        return 'Markets must be symbols like GOLD, up to 100';
    if (!(Number.isInteger(r.minHoldSeconds) && r.minHoldSeconds >= 0 && r.minHoldSeconds <= 86_400))
        return 'Minimum hold must be 0–86,400 seconds';
    if (c.cadence === 'welcome') {
        if (!(Number.isInteger(r.welcomeDays) && r.welcomeDays >= 1 && r.welcomeDays <= 365))
            return 'Welcome campaigns need 1–365 days from the first deposit';
    }
    else if (r.welcomeDays !== null)
        return 'Days from the first deposit are for welcome campaigns only';
    if (!Array.isArray(c.steps) || c.steps.length < 1 || c.steps.length > 8)
        return 'Keep between 1 and 8 steps';
    for (let i = 0; i < c.steps.length; i++) {
        const s = c.steps[i];
        const label = `Step ${i + 1}`;
        if (!(typeof s.volume === 'number' && Number.isFinite(s.volume) && s.volume >= 100 && s.volume <= 1_000_000_000_000))
            return `${label}: volume must be at least $100`;
        if (i > 0 && !(s.volume > c.steps[i - 1].volume))
            return `${label}: volume must be higher than step ${i}`;
        if (!VR_KINDS.includes(s.kind))
            return `${label}: choose a reward`;
        if (!(typeof s.usd === 'number' && s.usd > 0 && s.usd <= MAX_USD))
            return `${label}: ${s.kind === 'rebate' ? 'limit' : 'amount'} must be above $0 and at most $${MAX_USD.toLocaleString('en-US')}`;
        if (Math.round(s.usd * 100) !== s.usd * 100)
            return `${label}: use at most 2 decimal places`;
        if (s.kind === 'rebate' && !(typeof s.pct === 'number' && s.pct > 0 && s.pct <= 50))
            return `${label}: a rebate pays back 1–50% of fees and spread`;
        if (s.kind !== 'cash' && !(Number.isInteger(s.days) && s.days >= 1 && s.days <= 90))
            return `${label}: ${s.kind === 'bonus' ? 'a bonus' : 'a rebate'} lasts 1–90 days`;
    }
    const e = c.eligibility;
    if (!e || typeof e !== 'object')
        return 'Set who can join';
    if (e.minTier !== null && !(typeof e.minTier === 'string' && TIER_RE.test(e.minTier) && (!tierIds || tierIds.includes(e.minTier))))
        return 'Unknown tier';
    if (!Array.isArray(e.excludedCountries) || e.excludedCountries.some((x) => !CC_RE.test(x)))
        return 'Countries must be 2-letter codes like GB';
    if (!(Number.isInteger(c.claimDays) && c.claimDays >= 1 && c.claimDays <= 90))
        return 'Clients get 1–90 days to claim';
    if (c.holdAbove !== null && !(typeof c.holdAbove === 'number' && c.holdAbove >= 0 && c.holdAbove <= MAX_USD))
        return 'The check threshold must be $0–$10,000, or empty for none';
    if (c.budgetUsd !== null && !(typeof c.budgetUsd === 'number' && c.budgetUsd > 0 && c.budgetUsd <= 10_000_000))
        return 'Budget must be above $0, or empty for no limit';
    const starts = Date.parse(c.startsAt);
    if (!Number.isFinite(starts))
        return 'Enter a start date';
    if (c.endsAt !== null) {
        const ends = Date.parse(c.endsAt);
        if (!Number.isFinite(ends) || ends <= starts)
            return 'The end must be after the start';
    }
    if (c.cadence === 'event' && c.endsAt === null)
        return 'An event needs an end date';
    return null;
}
/** Starting point for a new campaign of each kind in the admin editor. */
export function campaignTemplate(cadence, now = new Date()) {
    const base = {
        cadence,
        name: '',
        description: '',
        rules: { weights: { ...DEFAULT_VR_WEIGHTS }, symbols: [], minHoldSeconds: DEFAULT_VR_MIN_HOLD_SECONDS, welcomeDays: cadence === 'welcome' ? 30 : null },
        steps: [
            { volume: 10_000, kind: 'cash', usd: 2 },
            { volume: 50_000, kind: 'cash', usd: 8 },
        ],
        eligibility: { minTier: null, excludedCountries: [] },
        claimDays: 7,
        holdAbove: 100,
        budgetUsd: null,
        startsAt: now.toISOString(),
        endsAt: cadence === 'event' ? new Date(now.getTime() + 14 * DAY_MS).toISOString() : null,
        status: 'draft',
    };
    return base;
}
//# sourceMappingURL=volume-rewards.js.map